import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { 
  CheckCircle2, 
  XCircle, 
  HelpCircle, 
  Clock, 
  Loader2,
  Users
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  going: { label: 'Going', icon: CheckCircle2, color: 'bg-green-600', textColor: 'text-green-400' },
  not_going: { label: 'Not Going', icon: XCircle, color: 'bg-red-600', textColor: 'text-red-400' },
  maybe: { label: 'Maybe', icon: HelpCircle, color: 'bg-amber-600', textColor: 'text-amber-400' },
  pending: { label: 'Pending', icon: Clock, color: 'bg-slate-600', textColor: 'text-slate-400' },
};

export default function RSVPPanel({ familyId, playerId, scheduleId, mode = 'family' }) {
  const { user } = useAuth();
  const { isDark } = useTheme();
  const [rsvps, setRsvps] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null);

  const fetchRSVPs = useCallback(async () => {
    try {
      let url;
      if (mode === 'schedule' && scheduleId) {
        url = `${API_URL}/api/rsvp/schedule/${scheduleId}`;
      } else if (mode === 'player' && playerId) {
        url = `${API_URL}/api/rsvp/player/${playerId}`;
      } else if (familyId) {
        url = `${API_URL}/api/rsvp/family/${familyId}`;
      } else {
        setLoading(false);
        return;
      }

      const { data } = await axios.get(url);

      if (mode === 'schedule') {
        setRsvps(data.rsvps || []);
        setSummary(data.summary || null);
      } else if (mode === 'family') {
        setRsvps(data || []);
      } else {
        setRsvps(data || []);
      }
    } catch (err) {
      console.error('Failed to fetch RSVPs:', err);
    } finally {
      setLoading(false);
    }
  }, [familyId, playerId, scheduleId, mode]);

  useEffect(() => {
    fetchRSVPs();
  }, [fetchRSVPs]);

  const updateRSVP = async (rsvpId, status) => {
    setUpdating(rsvpId);
    try {
      await axios.put(`${API_URL}/api/rsvp/${rsvpId}`, { status });
      fetchRSVPs();
    } catch (err) {
      console.error('Failed to update RSVP:', err);
    } finally {
      setUpdating(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-6 w-6 animate-spin text-amber-500" />
      </div>
    );
  }

  // Schedule view - shows all RSVPs for one event
  if (mode === 'schedule') {
    return (
      <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}>
        <CardHeader>
          <CardTitle className={`flex items-center gap-2 text-lg ${isDark ? 'text-white' : 'text-slate-900'}`}>
            <Users className="h-5 w-5 text-blue-500" />
            Availability
          </CardTitle>
        </CardHeader>
        <CardContent>
          {summary && (
            <div className="grid grid-cols-4 gap-2 mb-4">
              {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                <div key={key} className={`text-center p-2 rounded-lg ${isDark ? 'bg-slate-700/50' : 'bg-slate-100'}`}>
                  <p className={`text-lg font-bold ${cfg.textColor}`}>{summary[key] || 0}</p>
                  <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{cfg.label}</p>
                </div>
              ))}
            </div>
          )}
          
          {rsvps.length === 0 ? (
            <p className={`text-center py-4 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
              No RSVPs yet
            </p>
          ) : (
            <div className="space-y-2">
              {rsvps.map((rsvp) => {
                const cfg = STATUS_CONFIG[rsvp.status] || STATUS_CONFIG.pending;
                const Icon = cfg.icon;
                return (
                  <div 
                    key={rsvp.id}
                    className={`flex items-center justify-between p-3 rounded-lg ${isDark ? 'bg-slate-700/30' : 'bg-slate-50'}`}
                    data-testid={`rsvp-${rsvp.id}`}
                  >
                    <div className="flex items-center gap-3">
                      {rsvp.jersey_number && (
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${isDark ? 'bg-slate-600 text-white' : 'bg-slate-200 text-slate-700'}`}>
                          #{rsvp.jersey_number}
                        </div>
                      )}
                      <span className={isDark ? 'text-white' : 'text-slate-900'}>{rsvp.player_name || 'Player'}</span>
                    </div>
                    <Badge className={cfg.color}>
                      <Icon className="h-3 w-3 mr-1" />
                      {cfg.label}
                    </Badge>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  // Family view - grouped by player
  if (mode === 'family') {
    const playerGroups = Array.isArray(rsvps) ? rsvps : [];
    
    if (playerGroups.length === 0) {
      return (
        <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}>
          <CardHeader>
            <CardTitle className={`flex items-center gap-2 text-lg ${isDark ? 'text-white' : 'text-slate-900'}`}>
              <CheckCircle2 className="h-5 w-5 text-green-500" />
              Availability
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className={`text-center py-6 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
              <Clock className="h-10 w-10 mx-auto mb-2 opacity-50" />
              <p>No upcoming events to RSVP for</p>
              <p className="text-sm">RSVPs will appear once games are scheduled</p>
            </div>
          </CardContent>
        </Card>
      );
    }

    return (
      <div className="space-y-4">
        {playerGroups.map((group) => (
          <Card key={group.player_id} className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}>
            <CardHeader className="pb-2">
              <CardTitle className={`text-base ${isDark ? 'text-white' : 'text-slate-900'}`}>
                {group.player_name}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {group.rsvps.map((rsvp) => {
                const cfg = STATUS_CONFIG[rsvp.status] || STATUS_CONFIG.pending;
                return (
                  <div 
                    key={rsvp.id}
                    className={`flex items-center justify-between p-3 rounded-lg mb-2 ${isDark ? 'bg-slate-700/30' : 'bg-slate-50'}`}
                    data-testid={`rsvp-${rsvp.id}`}
                  >
                    <div>
                      <p className={`text-sm ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                        {rsvp.schedule?.game_datetime ? new Date(rsvp.schedule.game_datetime).toLocaleDateString() : 'TBD'}
                        {rsvp.schedule?.is_home ? ' (Home)' : ' (Away)'}
                      </p>
                    </div>
                    <div className="flex items-center gap-1">
                      {['going', 'maybe', 'not_going'].map((status) => {
                        const btnCfg = STATUS_CONFIG[status];
                        const BtnIcon = btnCfg.icon;
                        const isActive = rsvp.status === status;
                        return (
                          <Button
                            key={status}
                            size="sm"
                            variant={isActive ? 'default' : 'ghost'}
                            className={isActive ? btnCfg.color : ''}
                            onClick={() => updateRSVP(rsvp.id, status)}
                            disabled={updating === rsvp.id}
                            data-testid={`rsvp-${rsvp.id}-${status}`}
                          >
                            {updating === rsvp.id ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                              <BtnIcon className="h-3 w-3" />
                            )}
                          </Button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return null;
}
